// Copyright (c) 2023 MOCA Systems, Inc. All rights reserved.

import { map, take } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import * as appStrings from '@touchplan/strings';
import { emailFormatValidator } from '../../../form-utils/validators';
import { MaterialModule } from 'src/app/common/material/material-module';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { AccountCreationStore } from '../../services/account-creation-store.service';
import { AccountClaimsValidationService } from '../../services/account-claims-validation.service';
import { AuthOutputs } from '../../services/auth-outputs.service';
import { AuthService } from 'src/app/services/auth.service';
import { BannerMessageHandler } from '../../../logging/banner-message-handler';
import { ILoggerService } from '@touchplan/logging';
import { FirebaseCloudFunctionsService } from 'src/app/services/firebase-cloud-functions/cloud-functions.service';

@Component({
	selector: 'app-sign-up-validation',
	standalone: true,
	imports: [CommonModule, RouterModule, MaterialModule, PresentationModule, ReactiveFormsModule],
	providers: [
		BannerMessageHandler,
		AccountClaimsValidationService,
		LoggerServiceFactory.provide('Auth Sign Up Validation Component'),
	],
	templateUrl: './sign-up-validation.component.html',
	styleUrls: ['./sign-up-validation.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignUpValidationComponent implements OnInit {
	protected appStrings = appStrings;
	private logger: ILoggerService = LoggerServiceFactory.create('SignUpValidationComponent');

	public signUpValidationForm = this.formBuilder.group({
		email: ['', [Validators.required, emailFormatValidator()]],
	});

	constructor(
		private readonly accountCreationStore: AccountCreationStore,
		private readonly authOutputs: AuthOutputs,
		private readonly formBuilder: FormBuilder,
		//...kinda silly this needs to be injected to do a relative redirect
		private readonly activatedRoute: ActivatedRoute,
		private readonly accountClaimsValidationService: AccountClaimsValidationService,
		private readonly authService: AuthService,
		private readonly bannerMessageHandler: BannerMessageHandler,
		private readonly firebaseCloudFunctionsService: FirebaseCloudFunctionsService
	) {}

	public ngOnInit(): void {
		this.firebaseCloudFunctionsService
			.checkCloudConnectivity()
			.pipe(take(1))
			.subscribe({
				error: () =>
					this.bannerMessageHandler.displayErrorMessage(
						new Error('Cannot connect to backend. Please contact customer support.')
					),
			});
	}

	public continueSignUp(): void {
		if (this.signUpValidationForm.invalid) {
			return;
		}
		const email = this.signUpValidationForm.get('email').value;
		this.accountCreationStore.setLoadingState(true);
		forkJoin({
			tenant: this.accountClaimsValidationService.validateAccountClaimsFromEmail$(email),
			hasClaimUsers: this.authService.queryTenantViaEmail(email).pipe(
				take(1),
				map(
					(tenantInfo) =>
						tenantInfo && tenantInfo.claimedUsers && Object.keys(tenantInfo.claimedUsers).length > 0
				)
			),
		}).subscribe({
			next: ({ tenant, hasClaimUsers }) => {
				this.accountCreationStore.setLoadingState(false);
				this.accountCreationStore.collectEmail(email);
				// if user has claim users and it is on default tenant it means that they have an account so no signup is required
				if ((!tenant && hasClaimUsers) || (tenant && tenant.isDefaultTenant && hasClaimUsers)) {
					this.bannerMessageHandler.displayWarningMessage(
						appStrings.SIGN_UP_VALIDATION_USER_HAS_PENDING_MIGRATION
					);
					this.authOutputs.finishLoginNotSSO(this.activatedRoute);
				} else {
					this.authOutputs.continueSignUp(tenant, this.activatedRoute);
				}
			},
			error: (err) => {
				this.logger.error(err);
				this.accountCreationStore.setLoadingState(false);
			},
		});
	}
}
